/**
 * The API error contract (Phase 1b §3.2), as data.
 *
 * ── WHY THIS FILE EXISTS AT ALL (D5) ────────────────────────────────────────
 * D5 was decided as plain JavaScript with JSDoc rather than TypeScript, and
 * this module is the half of that decision that carries the weight. Every slug
 * the API can return is enumerated here once, so a component branches on
 * `ERR.NOT_DRAFT` — which fails loudly if misspelled — rather than on a string
 * literal, which fails silently by simply never matching.
 *
 * ── BRANCH ON THE SLUG, NEVER THE PROSE ─────────────────────────────────────
 * Every failure leaves the API as `{ error: <slug>, message: <prose> }`
 * (Phase 0 §3.5 rule 4). The prose is written for humans and will be reworded;
 * the slug is the contract. It is also the contract the MCP connector and the
 * ElevenLabs voice agent will branch on, which is why it is stable.
 *
 * ── COMPLETENESS IS THE POINT ───────────────────────────────────────────────
 * The list below was read from the API source, not from a document. A UI that
 * handles only the well-known half falls through to a generic error on
 * ordinary workflow states — "this estimate was already sent" rendered as
 * "Something went wrong" is the difference between a product that feels
 * finished and one that does not.
 */

/** @typedef {string} ErrorSlug */

/** Every slug the API can return. @type {Readonly<Record<string, ErrorSlug>>} */
export const ERR = Object.freeze({
  // --- 401 auth --------------------------------------------------------
  MISSING_TOKEN: 'missing_token',
  INVALID_TOKEN: 'invalid_token',

  // --- 403 identity and permission -------------------------------------
  NO_TENANT_CLAIM: 'no_tenant_claim',
  TENANT_MISMATCH: 'tenant_mismatch',
  FEATURE_NOT_ENABLED: 'feature_not_enabled',
  NOT_PERMITTED: 'not_permitted',
  // Phase 9 D9.1/D9.3 — the Operations zone gate. DISTINCT from NOT_PERMITTED
  // on purpose: that slug's copy says "only the workspace owner can do that",
  // which is false here. Operations is a per-user GRANT, not a role tier, so a
  // granted office manager is not an owner and a revoked one should be told to
  // ask for the grant back rather than to go find the owner for every task.
  OPERATIONS_ACCESS_REQUIRED: 'operations_access_required',

  // --- 402 subscription (Phase 0b) --------------------------------------
  // Not permission failures and not bugs — the workspace is fine, the
  // subscription is not. They get their own status code (402) and their own
  // screen, because the recovery action is "update a card", which no other
  // failure in this list shares.
  SUBSCRIPTION_SUSPENDED: 'subscription_suspended',
  SUBSCRIPTION_CANCELED: 'subscription_canceled',
  SUBSCRIPTION_INACTIVE: 'subscription_inactive',

  // --- billing surface ---------------------------------------------------
  NO_STRIPE_CUSTOMER: 'no_stripe_customer',
  INVALID_TIER: 'invalid_tier',
  STRIPE_NOT_CONFIGURED: 'stripe_not_configured',
  PRICE_NOT_CONFIGURED: 'price_not_configured',
  STRIPE_KEY_PERMISSION: 'stripe_key_permission',
  STRIPE_KEY_INVALID: 'stripe_key_invalid',
  STRIPE_REQUEST_INVALID: 'stripe_request_invalid',

  // --- 404 --------------------------------------------------------------
  NO_SUCH_WORKSPACE: 'no_such_workspace',
  NOT_FOUND: 'not_found',

  // --- 409 workflow state — NOT errors, states ---------------------------
  NOT_DRAFT: 'not_draft',
  NOT_ACCEPTABLE: 'not_acceptable',
  NOT_DECLINABLE: 'not_declinable',
  ESTIMATE_NOT_ACCEPTED: 'estimate_not_accepted',
  CANNOT_REVERT: 'cannot_revert',
  // Phase 2: the payments gate. One slug for pending AND disabled — the API
  // refuses both identically and the response carries a `status` field the
  // UI branches on for copy. Deliberately 409/WORKFLOW, never 402: the
  // workspace and subscription are fine, one button is unavailable
  // (phase-2-payments.md §4.3, amendment D).
  PAYMENTS_NOT_ACTIVE: 'payments_not_active',
  // Phase 2, D17: DISTINCT from NOT_DRAFT on purpose. A signed estimate is
  // refused for a stronger reason than "not a draft" — the stored
  // document_hash attests to exactly these numbers, and the UI's copy points
  // at a supplemental estimate instead of "create a new version".
  ESTIMATE_SIGNED_IMMUTABLE: 'estimate_signed_immutable',
  // Phase 2, item 6: share links exist only for estimates a customer can act
  // on. Draft (not yet sent) and closed estimates refuse issuance.
  ESTIMATE_NOT_SHAREABLE: 'estimate_not_shareable',
  // Phase 2, D9/C9: an expired share link is a DISTINCT public state (410),
  // never the 404 that unknown and revoked tokens share. The dashboard never
  // receives it (public surface only); enumerated so the contract has one
  // home.
  LINK_EXPIRED: 'link_expired',

  // --- 409 money path (Phase 2 Session 1b, items 11 + 13) ----------------
  // These are workflow states, not failures: the contractor asked for
  // something reasonable that the money says cannot happen.
  //
  // C16 replaced invoices' NOT_DRAFT with this: editing is now allowed on
  // sent/partial/overdue and refused only once the invoice is settled or
  // closed. NOT_DRAFT survives for estimates and for sending.
  INVOICE_LOCKED: 'invoice_locked',
  // A payment cannot be recorded against an invoice that was written off or
  // voided — the amount stopped being owed.
  INVOICE_CLOSED: 'invoice_closed',
  // D19/D24.4: partial, paid and refunded are DERIVED from the payment
  // ledger. Marking an invoice paid means recording the payment that paid it.
  // Replaces Phase 1's hand-set `paid`.
  STATUS_DERIVED_FROM_PAYMENTS: 'status_derived_from_payments',
  // D24.3: more than is left to refund on that one payment.
  REVERSAL_EXCEEDS_PAYMENT: 'reversal_exceeds_payment',
  // D24.2: more than was ever collected on that invoice. Raised from inside
  // the recompute's locked statement, so it is a guarantee and not a check.
  REVERSAL_EXCEEDS_CAPTURED: 'reversal_exceeds_captured',
  // D24.7: the payment carries a Maverick transaction id, so returning it
  // needs the processor — which is Session 2, and gated on C13's credential.
  PROCESSOR_REVERSAL_UNAVAILABLE: 'processor_reversal_unavailable',
  CANNOT_REVERSE_A_REVERSAL: 'cannot_reverse_a_reversal',
  // D24.5: one idempotency key, one money movement. Reaching this means the
  // key was reused for a genuinely different payment.
  DUPLICATE_IDEMPOTENCY_KEY: 'duplicate_idempotency_key',
  // 400, but it belongs beside the others: a manual payment's amount is the
  // fact being recorded, so it is validated rather than derived.
  INVALID_AMOUNT: 'invalid_amount',

  // --- payments boarding (Phase 2 item 16a — the Settings payments card) ---
  // These existed on the API from Session 1a and had NOTHING calling them, so
  // none of them was enumerated here. Added with the surface that reads them.
  //
  // The first two are 503 and mean OUR deployment is wrong, not the tenant's
  // workspace — the copy says so, because "payments boarding is not
  // configured" reads as the contractor's fault otherwise.
  MAVERICK_NOT_CONFIGURED: 'maverick_not_configured',
  MAVERICK_MISCONFIGURED: 'maverick_misconfigured',
  // 502. Maverick answered badly or not at all. Retryable, and never rendered
  // with Maverick's own words (G8 — its strings are truncated UI labels).
  MAVERICK_ERROR: 'maverick_error',
  // 409s. Both are ordinary states reached by clicking twice or by coming back
  // to a tab that has gone stale.
  PAYMENTS_ALREADY_ACTIVE: 'payments_already_active',
  BOARDING_IN_PROGRESS: 'boarding_in_progress',

  // --- tenant settings (Phase 2 item 16a, §5 items 6a + 6c) ----------------
  // D22.7: a deposit minimum below what the processor can charge. The API
  // message carries the actual floor figure, so there is deliberately no COPY
  // entry — a static sentence here would have to omit the number that makes
  // it useful.
  DEPOSIT_MINIMUM_BELOW_FLOOR: 'deposit_minimum_below_floor',
  // The 16 KB tenants_branding_size_check, surfaced as a sentence rather than
  // as a raw 23514.
  BRANDING_TOO_LARGE: 'branding_too_large',

  // --- document delivery (Phase 2 item 17) --------------------------------
  // 503s: OUR deployment is wrong (no RESEND_API_KEY, no API_PUBLIC_URL), not
  // the tenant's workspace — same posture as MAVERICK_NOT_CONFIGURED.
  DELIVERY_NOT_CONFIGURED: 'delivery_not_configured',
  // 502: Resend answered badly or not at all. Retryable.
  DELIVERY_PROVIDER_ERROR: 'delivery_provider_error',
  // 502: the send itself failed after everything was configured correctly.
  // The document was NOT changed — recorded in document_deliveries as
  // status=failed either way, so the history shows the attempt.
  DELIVERY_FAILED: 'delivery_failed',
  EMAIL_ALREADY_REGISTERED: 'email_already_registered',
  SEAT_LIMIT_REACHED: 'seat_limit_reached',
  // 409, Phase 9 D9.1. The owner's Operations access follows their role and is
  // not a stored grant, so there is nothing to switch off. A workflow state,
  // not a failure: the owner asked something reasonable about a row whose
  // answer is already yes and always will be.
  OPERATIONS_ACCESS_OWNER_IMPLICIT: 'operations_access_owner_implicit',
  // 400, platform console only (D37): SMS was set to active without the
  // fields the send path needs. Inline on the admin SMS card, not a toast —
  // the operator has to go back to the Twilio console to get the missing SID.
  SMS_IDENTITY_INCOMPLETE: 'sms_identity_incomplete',
  // 409, platform console only (D41, item 27). Two states, one slug, because
  // the operator's next move is the same for both: the merchant webhook step
  // is not recorded, so `active` is refused — or it is recorded and cannot be
  // withdrawn while the tenant is live. Raised by a database trigger, not by a
  // controller, which is deliberate: see services/maverickWebhookGate.js.
  MAVERICK_WEBHOOK_NOT_CONFIRMED: 'maverick_webhook_not_confirmed',
  // 409, platform console only. Recording a webhook against a workspace with
  // no current boarding application — there is no merchant portal in which one
  // could have been created.
  MAVERICK_NOT_BOARDED: 'maverick_not_boarded',

  // --- 400 validation ----------------------------------------------------
  INVALID_LINE_ITEMS: 'invalid_line_items',
  MISSING_FIELD: 'missing_field',
  INVALID_FIELD: 'invalid_field',
  INVALID_DATE_RANGE: 'invalid_date_range',
  MISSING_CONTACT: 'missing_contact',
  INVALID_WINDOW: 'invalid_window',
  UNKNOWN_JOB: 'unknown_job',
  UNKNOWN_ESTIMATE: 'unknown_estimate',
  UNKNOWN_STAGE: 'unknown_stage',
  PARTIAL_NOT_SUPPORTED: 'partial_not_supported',
  USE_STAGE_ENDPOINT: 'use_stage_endpoint',
  NOTHING_TO_UPDATE: 'nothing_to_update',
  MISSING_HOST: 'missing_host',

  // --- public lead route -------------------------------------------------
  // Never reached by the authenticated dashboard, but Settings must be able to
  // EXPLAIN them: they are how the onboarding funnel fails, and
  // origin_not_allowed is the specific failure D3 exists to prevent.
  MISSING_FORM_KEY: 'missing_form_key',
  INVALID_FORM_KEY: 'invalid_form_key',
  ORIGIN_NOT_ALLOWED: 'origin_not_allowed',
  RATE_LIMITED: 'rate_limited',

  // --- server ------------------------------------------------------------
  INTERNAL_ERROR: 'internal_error',
  REQUEST_FAILED: 'request_failed',

  // --- client-side only --------------------------------------------------
  // Not from the API. A request that never got a response at all — offline,
  // DNS failure, or CORS refusing before the call was made. Distinguished
  // because "you appear to be offline" and "the server rejected that" need
  // different words, and a contractor in a basement will see the first.
  NETWORK: 'network_unreachable'
})

/**
 * Slugs meaning the session is over. The API client signs out on these rather
 * than retrying — a retry loop against an expired token is how a login screen
 * turns into a flicker.
 */
export const FATAL_SESSION = Object.freeze([ERR.TENANT_MISMATCH])

/**
 * Slugs the UI must render as a full-screen explanation rather than a toast.
 * Each means the workspace itself is unusable, so there is no screen behind
 * the message worth showing.
 */
export const FULL_SCREEN = Object.freeze([ERR.NO_TENANT_CLAIM, ERR.NO_SUCH_WORKSPACE])

/**
 * Subscription blocks (Phase 0b).
 *
 * These can appear MID-SESSION, which none of the other full-screen slugs can:
 * a webhook may suspend the workspace while the user has the dashboard open, so
 * the next ordinary call returns 402 out of nowhere. That is why this is a
 * separate list rather than more entries in FULL_SCREEN — the UI has to treat
 * "was fine a moment ago" as a first-class case and re-check status rather than
 * assume a stale boot payload.
 */
export const SUBSCRIPTION_BLOCK = Object.freeze([
  ERR.SUBSCRIPTION_SUSPENDED,
  ERR.SUBSCRIPTION_CANCELED,
  ERR.SUBSCRIPTION_INACTIVE
])

/**
 * Workflow states. Rendered inline and actionable, never as a failure —
 * the user did something reasonable that the current state does not allow.
 */
export const WORKFLOW = Object.freeze([
  ERR.NOT_DRAFT,
  ERR.NOT_ACCEPTABLE,
  ERR.NOT_DECLINABLE,
  ERR.ESTIMATE_NOT_ACCEPTED,
  ERR.CANNOT_REVERT,
  ERR.PARTIAL_NOT_SUPPORTED,
  ERR.PAYMENTS_NOT_ACTIVE,
  ERR.ESTIMATE_SIGNED_IMMUTABLE,
  ERR.ESTIMATE_NOT_SHAREABLE,
  // Phase 2 money path. Every one of these is "the money says no", which is a
  // state to explain inline, never a red failure toast.
  ERR.INVOICE_LOCKED,
  ERR.INVOICE_CLOSED,
  ERR.STATUS_DERIVED_FROM_PAYMENTS,
  ERR.REVERSAL_EXCEEDS_PAYMENT,
  ERR.REVERSAL_EXCEEDS_CAPTURED,
  ERR.PROCESSOR_REVERSAL_UNAVAILABLE,
  ERR.CANNOT_REVERSE_A_REVERSAL,
  ERR.DUPLICATE_IDEMPOTENCY_KEY,
  // Boarding. Both mean "the state moved on since this screen rendered",
  // which is a thing to explain inline, not a failure.
  ERR.PAYMENTS_ALREADY_ACTIVE,
  ERR.BOARDING_IN_PROGRESS,
  ERR.SEAT_LIMIT_REACHED,
  // Phase 9. "That is already true and cannot be otherwise", which is a state
  // to explain inline rather than a red failure.
  ERR.OPERATIONS_ACCESS_OWNER_IMPLICIT,
  // Phase 2 item 27. "A step outside MBOS has not been done yet" — the most
  // literal workflow state in the list, and the one with a person's name on it.
  ERR.MAVERICK_WEBHOOK_NOT_CONFIRMED,
  ERR.MAVERICK_NOT_BOARDED
])

/**
 * Slugs that indicate a bug in THIS code rather than anything the user did.
 * Surfaced loudly in development so they are found during the build, not in
 * a support ticket six weeks later.
 */
export const UI_BUG = Object.freeze([
  ERR.USE_STAGE_ENDPOINT,
  ERR.NOTHING_TO_UPDATE,
  ERR.MISSING_HOST,
  // Reachable only if a control was shown that should have been gated —
  // the gate failing open is itself the bug.
  ERR.FEATURE_NOT_ENABLED
  //
  // NOT_PERMITTED was here and was REMOVED in Phase 2 Session 1b, when roles
  // became real (item 12 / C14). It used to mean "a control was shown that
  // should have been gated", which was fair while nothing read
  // `tenant_users.role`. Now there is a genuine non-bug path to it: the role
  // gate does a LIVE database read precisely so a revoked permission takes
  // effect immediately rather than waiting for a token refresh, which means a
  // member whose role changed while the dashboard was open will legitimately
  // receive it. Shouting "UI bug" at a designed outcome trains people to
  // ignore the shout. It has a COPY entry instead.
])

/**
 * Human copy for slugs whose API prose is too terse or too technical to show a
 * contractor. Anything absent here falls back to the API's own message, which
 * is written well enough for most cases.
 *
 * These are deliberately about what to DO, not about what went wrong.
 */
const COPY = Object.freeze({
  [ERR.NOT_DRAFT]: 'This has already been sent, so it can no longer be edited. Create a new version instead.',
  [ERR.NOT_ACCEPTABLE]: 'This estimate is not in a state that can be accepted.',
  [ERR.NOT_DECLINABLE]: 'This estimate is not in a state that can be declined.',
  [ERR.ESTIMATE_NOT_ACCEPTED]: 'The estimate has to be accepted before it can be invoiced.',
  [ERR.CANNOT_REVERT]: 'An invoice cannot move backwards to that status.',
  [ERR.PARTIAL_NOT_SUPPORTED]: 'Partial payments are not supported yet — they arrive with payment collection.',
  // Phase 2. One slug, two states: the response's `status` field says which.
  // paymentsNotActiveMessage() below picks the right sentence; this entry is
  // the fallback when the status field is absent.
  [ERR.PAYMENTS_NOT_ACTIVE]: 'Payments are not available for this workspace yet.',
  // Phase 2, D17. Deliberately different advice from NOT_DRAFT: the signed
  // record cannot change, and the right move for added work is a supplemental
  // estimate, not a rewrite.
  [ERR.ESTIMATE_SIGNED_IMMUTABLE]:
    'This estimate has been signed, so its contents are locked. Add extra work as a supplemental estimate instead.',
  [ERR.ESTIMATE_NOT_SHAREABLE]:
    'Send the estimate first, then share it for signing. Signed and closed estimates cannot be shared.',
  [ERR.LINK_EXPIRED]:
    'That link has expired. Issue a fresh share link and send it again.',

  // Phase 2 money path. Written as what to DO, and careful never to imply
  // that money moved or did not move — a contractor who misreads a refund
  // message has a customer on the phone about it.
  [ERR.NOT_PERMITTED]:
    'Only the workspace owner can do that. Ask them to sign in and complete it.',
  [ERR.INVOICE_LOCKED]:
    'This invoice is settled or closed, so its contents can no longer change. ' +
    'Add extra work to a new invoice instead.',
  [ERR.INVOICE_CLOSED]:
    'This invoice was written off or voided, so nothing is owed on it. ' +
    'Reopen it by creating a new invoice for the work.',
  [ERR.STATUS_DERIVED_FROM_PAYMENTS]:
    'An invoice becomes paid when a payment is recorded against it. ' +
    'Record the payment and the status follows.',
  [ERR.REVERSAL_EXCEEDS_PAYMENT]:
    'That is more than is left to refund on this payment. Check what has ' +
    'already been refunded against it.',
  [ERR.REVERSAL_EXCEEDS_CAPTURED]:
    'That would return more than was ever collected on this invoice.',
  [ERR.PROCESSOR_REVERSAL_UNAVAILABLE]:
    'This payment was taken by card, so it has to be refunded through the ' +
    'card processor. That is not available in MBOS yet.',
  [ERR.CANNOT_REVERSE_A_REVERSAL]:
    'That row is already a refund. Reverse the original payment instead.',
  [ERR.DUPLICATE_IDEMPOTENCY_KEY]:
    'That submission was already recorded. Reload to see the current total.',
  [ERR.INVALID_AMOUNT]:
    'Enter an amount greater than zero.',
  // Phase 2 item 16a — boarding and settings.
  [ERR.MAVERICK_NOT_CONFIGURED]:
    'Payment setup is not switched on for this deployment yet. Nothing you did caused this.',
  [ERR.MAVERICK_MISCONFIGURED]:
    'Payment setup is misconfigured on our side, so we have stopped rather than ' +
    'open an account on the wrong terms. Nothing you did caused this, and we are on it.',
  [ERR.MAVERICK_ERROR]:
    'The payments provider did not respond properly. Nothing has been changed. Try again shortly.',
  [ERR.PAYMENTS_ALREADY_ACTIVE]:
    'Payments are already set up for this workspace.',
  [ERR.BOARDING_IN_PROGRESS]:
    'A payment setup request is already running. Give it a moment, then reload.',
  [ERR.BRANDING_TOO_LARGE]:
    'There is too much information here to save. Shorten the longer fields and try again.',
  [ERR.DELIVERY_NOT_CONFIGURED]:
    'Document delivery is not switched on for this deployment yet. Nothing you did caused this.',
  [ERR.DELIVERY_PROVIDER_ERROR]:
    'The email provider did not respond properly. Nothing was sent. Try again shortly.',
  [ERR.DELIVERY_FAILED]:
    'The message could not be sent. Nothing on the document changed, so it is safe to try again.',
  [ERR.EMAIL_ALREADY_REGISTERED]:
    'That email address is already associated with a different MBOS workspace. ' +
    'Each login can only belong to one workspace today. Invite them using a different email address, or contact support.',
  [ERR.SEAT_LIMIT_REACHED]:
    'This plan has no open user seats. Upgrade before inviting another login.',

  // Phase 9 D9.1. NOT a UI bug, for the same reason NOT_PERMITTED stopped
  // being one: Operations access is read live on every request precisely so a
  // revocation bites immediately, which means a user whose access was withdrawn
  // while they had the tab open will legitimately land here. The copy says who
  // can fix it, because the reader cannot.
  [ERR.OPERATIONS_ACCESS_REQUIRED]:
    'Operations is not switched on for your login. The workspace owner can turn ' +
    'it on for you under Settings, in Workspace users.',
  [ERR.OPERATIONS_ACCESS_OWNER_IMPLICIT]:
    'The workspace owner always has Operations access, so it cannot be switched off.',

  // Phase 2 item 27 (D41), platform console only. The copy names the step and
  // says where it happens, because the reader is an operator who has to go and
  // do something in a system that is not this one.
  [ERR.MAVERICK_WEBHOOK_NOT_CONFIRMED]:
    'The merchant\'s transaction webhook has not been recorded as configured, so ' +
    'payments cannot be made active for this workspace. It is created by hand in ' +
    'the merchant\'s own Maverick portal — there is no API for it.',
  [ERR.MAVERICK_NOT_BOARDED]:
    'This workspace has no current payments application, so there is no merchant ' +
    'portal in which a webhook could have been configured yet.',

  [ERR.MISSING_CONTACT]: 'Add an email address or a phone number so this person can be contacted.',
  [ERR.INVALID_WINDOW]: 'The end time has to be after the start time.',
  [ERR.INVALID_DATE_RANGE]: 'Choose a valid report date range.',
  [ERR.NO_TENANT_CLAIM]: 'Your login is not linked to a workspace yet.',
  [ERR.NO_SUCH_WORKSPACE]: 'There is no workspace at this address.',
  [ERR.RATE_LIMITED]: 'Too many requests just now. Give it a moment and try again.',
  [ERR.NETWORK]: 'Could not reach MBOS. Check your connection and try again.',
  [ERR.INTERNAL_ERROR]: 'Something went wrong on our end. Try again in a moment.',
  [ERR.REQUEST_FAILED]: 'That request could not be completed.',
  [ERR.ORIGIN_NOT_ALLOWED]:
    'That website address is not on this form key\'s allowed list, so the form ' +
    'will be rejected. Add it under "Where will this form live?".',

  // Phase 0b. Written as what to DO, and deliberately free of blame — a failed
  // card is usually a bank's fraud filter or an expiry date, not negligence.
  [ERR.SUBSCRIPTION_SUSPENDED]:
    'Your subscription could not be collected, so this workspace is paused. ' +
    'Updating your payment method restores it straight away.',
  [ERR.SUBSCRIPTION_CANCELED]:
    'This workspace\'s subscription has ended. Your data is safe — starting a ' +
    'subscription again restores access to it.',
  [ERR.SUBSCRIPTION_INACTIVE]:
    'This workspace does not have an active subscription yet.',
  [ERR.NO_STRIPE_CUSTOMER]:
    'This workspace has no billing account yet, so there is nothing to manage. ' +
    'Start a subscription first.',
  [ERR.STRIPE_NOT_CONFIGURED]:
    'Billing is not set up on this deployment yet. Nothing you did caused this.',
  [ERR.PRICE_NOT_CONFIGURED]:
    'That plan is not available for purchase yet. Nothing you did caused this.',
  [ERR.STRIPE_KEY_PERMISSION]:
    'Billing is misconfigured on our side and cannot complete that. Nothing you did caused this.',
  [ERR.STRIPE_KEY_INVALID]:
    'Billing is misconfigured on our side and cannot complete that. Nothing you did caused this.'
})

/**
 * The sentence to show a human for a given failure.
 * @param {{slug?: ErrorSlug, message?: string}|null|undefined} err
 * @returns {string}
 */
export function humanMessage (err) {
  if (!err) return COPY[ERR.REQUEST_FAILED]
  return COPY[err.slug] ?? err.message ?? COPY[ERR.REQUEST_FAILED]
}

/** @param {{slug?: ErrorSlug}|null} err */
export const isWorkflow = err => WORKFLOW.includes(err?.slug)

/** @param {{slug?: ErrorSlug}|null} err */
export const isFullScreen = err => FULL_SCREEN.includes(err?.slug)

/** @param {{slug?: ErrorSlug}|null} err */
export const isUiBug = err => UI_BUG.includes(err?.slug)

/**
 * The workspace is blocked on money, not on permission.
 * @param {{slug?: ErrorSlug}|null} err
 */
export const isSubscriptionBlock = err => SUBSCRIPTION_BLOCK.includes(err?.slug)

/**
 * Copy for the payments gate (Phase 2). The API returns ONE slug,
 * payments_not_active, with a `status` field distinguishing a boarding still
 * in underwriting from payments switched off — the two need different words
 * and different tones ("wait" vs "contact support"), which is why this is a
 * function rather than a COPY entry.
 * @param {{slug?: ErrorSlug, status?: string}|null|undefined} err
 * @returns {string}
 */
export function paymentsNotActiveMessage (err) {
  if (err?.status === 'disabled') {
    return 'Payments are switched off for this workspace. Contact support to restore them.'
  }
  if (err?.status === 'pending') {
    return 'Your payments account is still being reviewed. Collecting payments unlocks as soon as it is approved.'
  }
  return 'Payments are not set up for this workspace yet. Connect payments in Settings to start collecting.'
}
